import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import apiService from "../services/apiService";

function AIUsage() {
  const navigate = useNavigate();
  const [usageData, setUsageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchAIUsage() {
      setLoading(true);
      setError(null);
      try {
        const data = await apiService.getLLMUsageSummary();
        setUsageData(data);
      } catch (err) {
        setError("Failed to load AI usage data.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    fetchAIUsage();
  }, []);

  const formatPurpose = (purpose) => {
    return purpose
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (loading) {
    return (
      <div>
        <h1>AI Usage</h1>
        <p>Loading AI usage data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <h1>AI Usage</h1>
        <p className="error-message">{error}</p>
      </div>
    );
  }

  if (!usageData) {
    return (
      <div>
        <h1>AI Usage</h1>
        <p>No usage data available.</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "2rem" }}>
        <div>
          <h1>AI Usage</h1>
          <p style={{ color: "var(--text-light)", marginTop: "0.5rem" }}>
            Monitor and analyze your AI interactions and token consumption
          </p>
        </div>
        <button 
          className="btn btn-secondary" 
          onClick={() => navigate('/ai-usage-history')}
        >
          📊 View Detailed History
        </button>
      </div>

      {/* Quota Information */}
      {usageData.quota_info && (
        <div style={{ 
          padding: "2rem", 
          backgroundColor: "white", 
          borderRadius: "12px", 
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          marginBottom: "2rem",
          border: "1px solid #e0e0e0"
        }}>
          <h2 style={{ marginTop: 0, marginBottom: "1.5rem", fontSize: "1.25rem" }}>Token Quota</h2>
          
          <div style={{ marginBottom: "1.5rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.5rem" }}>
              <span style={{ fontWeight: "500" }}>Usage Progress</span>
              <span style={{ fontWeight: "600", color: usageData.quota_info.usage_percentage > 80 ? "#e53e3e" : "#48bb78" }}>
                {usageData.quota_info.usage_percentage.toFixed(1)}%
              </span>
            </div>
            <div style={{ 
              width: "100%", 
              height: "30px", 
              backgroundColor: "#e0e0e0", 
              borderRadius: "15px", 
              overflow: "hidden",
              position: "relative"
            }}>
              <div style={{ 
                width: `${Math.min(usageData.quota_info.usage_percentage, 100)}%`, 
                height: "100%", 
                backgroundColor: usageData.quota_info.usage_percentage > 80 ? "#e53e3e" : "#48bb78",
                transition: "width 0.3s ease"
              }}></div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: "0.5rem", fontSize: "0.9rem" }}>
              <span style={{ color: "var(--text-light)" }}>
                <strong>{usageData.quota_info.used_tokens.toLocaleString()}</strong> used
              </span>
              <span style={{ color: "var(--text-light)" }}>
                <strong>{usageData.quota_info.effective_quota.toLocaleString()}</strong> total
              </span>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "1rem" }}>
            <div>
              <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--text-light)" }}>Available Tokens</p>
              <p style={{ margin: "0.25rem 0 0 0", fontSize: "1.25rem", fontWeight: "600", color: "#48bb78" }}>
                {usageData.quota_info.available_tokens.toLocaleString()}
              </p>
            </div>
            <div>
              <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--text-light)" }}>Subscription Tokens</p>
              <p style={{ margin: "0.25rem 0 0 0", fontSize: "1.25rem", fontWeight: "600" }}>
                {usageData.quota_info.subscription_tokens.toLocaleString()}
              </p>
            </div>
            <div>
              <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--text-light)" }}>Bonus Tokens</p>
              <p style={{ margin: "0.25rem 0 0 0", fontSize: "1.25rem", fontWeight: "600", color: "#ed8936" }}>
                {usageData.quota_info.bonus_tokens.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Usage by Purpose */}
      {usageData.usage_by_purpose && usageData.usage_by_purpose.length > 0 && (
        <div style={{ 
          padding: "2rem", 
          backgroundColor: "white", 
          borderRadius: "12px", 
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          marginBottom: "2rem",
          border: "1px solid #e0e0e0"
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
            <h2 style={{ margin: 0, fontSize: "1.25rem" }}>Usage by Purpose</h2>
            <span style={{ fontSize: "0.9rem", color: "var(--text-light)" }}>
              <strong style={{ fontSize: "1.5rem", color: "#48bb78" }}>{usageData.usage_summary.total_entries.toLocaleString()}</strong> total requests
            </span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "1rem" }}>
            {usageData.usage_by_purpose.map((item, index) => (
              <div 
                key={index}
                style={{ 
                  padding: "1.25rem", 
                  backgroundColor: "#f7fafc", 
                  borderRadius: "8px",
                  border: "1px solid #e0e0e0"
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.75rem" }}>
                  <h4 style={{ margin: 0, fontSize: "0.9rem", fontWeight: "600" }}>
                    {formatPurpose(item.purpose)}
                  </h4>
                  <span style={{ 
                    padding: "0.25rem 0.5rem", 
                    backgroundColor: "#4299e1", 
                    color: "white", 
                    borderRadius: "12px", 
                    fontSize: "0.75rem",
                    fontWeight: "600"
                  }}>
                    {item.count}
                  </span>
                </div>
                <div style={{ fontSize: "0.85rem", color: "var(--text-light)" }}>
                  <p style={{ margin: "0.25rem 0" }}>
                    <strong>Total:</strong> {item.total_tokens.toLocaleString()} tokens
                  </p>
                  <p style={{ margin: "0.25rem 0" }}>
                    <strong>Avg:</strong> {Math.round(item.avg_tokens).toLocaleString()} tokens/request
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Activity */}
      {usageData.recent_entries && usageData.recent_entries.length > 0 && (
        <div style={{ 
          padding: "2rem", 
          backgroundColor: "white", 
          borderRadius: "12px", 
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          border: "1px solid #e0e0e0"
        }}>
          <h2 style={{ marginTop: 0, marginBottom: "1.5rem", fontSize: "1.25rem" }}>Recent Activity</h2>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            {usageData.recent_entries.map((entry) => (
              <div 
                key={entry.id}
                style={{ 
                  padding: "1rem", 
                  backgroundColor: "#f7fafc", 
                  borderRadius: "8px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  border: "1px solid #e0e0e0"
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.25rem" }}>
                    <span style={{ 
                      padding: "0.2rem 0.6rem", 
                      borderRadius: "12px", 
                      fontSize: "0.75rem",
                      backgroundColor: "#e3f2fd",
                      color: "#1976d2",
                      fontWeight: "500"
                    }}>
                      {formatPurpose(entry.purpose)}
                    </span>
                    <span style={{ fontSize: "0.85rem", color: "var(--text-light)" }}>
                      {new Date(entry.timestamp).toLocaleString()}
                    </span>
                  </div>
                  {entry.object_name && (
                    <p style={{ margin: "0.25rem 0 0 0", fontSize: "0.9rem", color: "#2d3748" }}>
                      {entry.object_name}
                    </p>
                  )}
                </div>
                <div style={{ textAlign: "right" }}>
                  <p style={{ margin: 0, fontSize: "1.1rem", fontWeight: "600", color: "#4299e1" }}>
                    {entry.total_tokens.toLocaleString()}
                  </p>
                  <p style={{ margin: "0.25rem 0 0 0", fontSize: "0.75rem", color: "var(--text-light)" }}>
                    tokens
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default AIUsage;